import { useState, useCallback, useMemo } from 'react';
import type { IValidator } from '../types';

export type Field<T> = {
  value: T;
  isValid: boolean;
  cleared?: boolean;
  error?: string;
  setError: (message: string) => void;
  update: (value: T) => void;
  clear: () => void;
  validate: () => boolean;
  onClear: (cb?: () => void) => void;
  addValidators: (validators: IValidator[]) => void;
};

export function useField<T>(initialValue: T): Field<T> {

  const [value, setValue] = useState<T>(initialValue);
  const [validators, setValidators] = useState<IValidator[]>([]);
  const [error, setError] = useState<string>();
  const [cleared, setCleared] = useState(false);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [clearCb, setClearCb] = useState<() => void>();
  
  const update = useCallback((value: T) => {
    setValue(value);
    setIsValid(true);
    setCleared(false);
    setError(undefined);
  }, []);

  const addValidators = useCallback((validators: IValidator[]) => {
    setValidators(validators);
  }, []);

  const validate = useCallback((): boolean => {

    for (const validator of validators) {
      if (!validator.validate(value as unknown as string)) {
        setIsValid(false);
        setError(validator.message);
        return false;
      }
    }

    setIsValid(true);
    setError(undefined);

    return true;
  }, [validators, value]);

  const setErr = useCallback((message: string) => {
    setIsValid(false);
    setError(message);
  }, []);

  const clear = useCallback(() => {
    setValue(initialValue);
    setIsValid(true);
    setCleared(true);
    setError(undefined);
    clearCb?.();
  }, [initialValue]);

  const onClear = useCallback((cb?: () => void) => {
    setClearCb(() => cb);
  }, []);

  return useMemo(() => ({
    value,
    isValid,
    cleared,
    error,
    setError: setErr,
    update,
    addValidators,
    validate,
    clear,
    onClear
  }), [value, isValid, error, cleared, update, addValidators, validate, setErr, clear, onClear]);
}
